<template>
  <v-card v-if="showAll" flat height="auto">
    <v-window v-model="step" touchless>
      <v-window-item :value="1">
        <v-form
          ref="formStep1"
          v-model="validStep1"
          @submit.prevent="
            () => {
              return false;
            }
          "
        >
          <advice v-if="restrictionAdvice" color="primary" class="my-3">
            <span
              v-html="
                $t('parking.restriction.advice', {
                  minEntryTime:
                    this.$options.filters.formatTimeRounded(minDate),
                  minEntryDate: this.$options.filters.formatDate(minDate)
                })
              "
            ></span>
          </advice>
          <v-card-title
            v-if="currentTitle"
            class="grey--text grey--lighten-1 body-1 pb-0 mt-3"
          >
            {{ $t(currentTitle) }}
          </v-card-title>

          <v-card-text>
            <reservation-date-time-picker
              ref="reservationDateTimePicker"
              :input-date="inputDate"
              :min-date="minDate"
              :input-time="inputTime"
              :parking-timezone="parking.timezone"
              @on-date-selected="onDateSelected"
              @on-time-selected="onTimeSelected"
            >
            </reservation-date-time-picker>
            <template v-if="parkingHasBle || parkingHasCamara">
              <reservation-plate-selection
                :key="plateSelectorKey"
                :plates="user.plates"
                :plate-code-selected="bookingData.plate"
                @on-plate-selected="onPlateSelected"
                @on-plate-added="onPlateAdded"
              >
              </reservation-plate-selection>
            </template>
            <template v-else-if="parkingHasApr">
              <reservation-tag-selection
                :tags="user.tags"
                :tag-code-selected="bookingData.tagSelected.code"
                :can-edit-plate="false"
                @on-tag-selected="onTagSelected"
                @on-plate-changed="onPlateChanged"
              >
              </reservation-tag-selection>
            </template>
          </v-card-text>

          <advice v-if="hasObservationAsWarning" color="warning" class="my-3">
            <p
              class="font-weight-medium"
              v-html="parking.description.observationWarning.title"
            ></p>
            <p v-html="parking.description.observationWarning.text"></p>

            <v-checkbox
              v-model="acceptWarningMessage"
              :label="$t('global.button.agree')"
              class="pa-0"
              :rules="warningRules"
            ></v-checkbox>
          </advice>
        </v-form>
      </v-window-item>

      <v-window-item :value="2">
        <v-card v-if="showClosingWarning" flat class="mb-3">
          <v-card-title primary-title class="pb-0">
            <div>
              <div class="title">{{ $t('global.closing.time') }}</div>
              <div class="subheading grey--text text--darken-1">
                {{ warningClosingTime | formatDateAsUtc($i18n.locale) }}
              </div>
            </div>
          </v-card-title>

          <v-card-text class="pb-0" v-html="closingWarningText"></v-card-text>

          <v-card-actions class="pl-3 pt-0">
            <v-checkbox
              v-model="acceptClosingTime"
              :label="$t('global.accept.closing.time')"
              hide-details
            ></v-checkbox>
          </v-card-actions>
        </v-card>
        <v-card flat class="mb-3">
          <v-card-title primary-title class="pb-0">
            <div>
              <div class="title">{{ $t('global.max.availability') }}</div>
              <div class="subheading grey--text text--darken-1">
                {{ warningMaxAvailability | formatDateAsUtc($i18n.locale) }}
              </div>
            </div>
          </v-card-title>

          <v-card-text
            class="pb-0"
            v-html="
              $t('reservation.warning.max.availability', {
                maxAvailability: maxAvailabilityFormatted,
                extraPrice: parking.minuteExtraPriceWithCurrency
              })
            "
          ></v-card-text>

          <v-card-actions class="pl-3 pt-0">
            <v-checkbox
              v-model="acceptMaxAvailability"
              :label="$t('global.accept.max.availability')"
              hide-details
            ></v-checkbox>
          </v-card-actions>
        </v-card>

      </v-window-item>

      <v-window-item :value="100">
        <advice color="warning">
          <p class="title pb-3" v-html="message"></p>
        </advice>

        <h3
          v-if="alternativeParkingList.length > 0"
          class="grey--text grey--lighten-1 body-1 mb-2 px-3 pt-3"
        >
          {{ $t('reservation.failed.alternatives') }}
        </h3>

        <alternative-parkings
          :parkings="alternativeParkingList"
          :date="booking.entryDate"
        ></alternative-parkings>
      </v-window-item>

      <v-window-item :value="200">
        <v-card flat class="mb-3">
          <v-card-title primary-title class="pb-0">
            <div>
              <div class="title">{{ $t('reservation.edit.success1') }}</div>
              <div class="subheading grey--text text--darken-1">
                {{ entryFormatted }}
              </div>
            </div>
          </v-card-title>

          <v-card-text>
            <p
              v-if="showClosingWarning"
              class="mb-0"
              v-html="successClosingText"
            ></p>

            <p :class="showClosingWarning ? 'mt-3 mb-0' : 'mb-0'">
              <span v-html="$t('reservation.success3')"></span>
              <span
                v-if="acceptMaxAvailability"
                v-html="
                  $t('reservation.success4', {
                    maxAvailabilityTime: maxAvailabilityTimeFormatted,
                    maxAvailabilityDate: maxAvailabilityDateFormatted
                  })
                "
              ></span>
            </p>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        v-if="step !== 200"
        flat
        color="primary"
        round
        class="initialize"
        @click="cancelEdit"
      >
        {{ $t('global.button.cancel') }}</v-btn
      >

      <v-btn
        v-if="step > 1 && step < 200"
        round
        flat
        color="primary"
        class="initialize white--text mr-4"
        @click="goToStep1()"
      >
        {{ backLabel() }}
      </v-btn>

      <v-btn
        v-if="showBookingButton"
        :disabled="!valid"
        :loading="isLoading"
        type="submit"
        color="accent"
        class="capitalize white--text mr-4"
        round
        outline
        @click="checkAvailability"
      >
        {{ $t('global.action.modify') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { EventBus } from '@/services/eventBus';
import AlternativeParkings from '@/components/commons/AlternativeParkings';
import ReservationDateTimePicker from '@/components/commons/ReservationDateTimePicker';
import keys from '@/store/keys';
import manageErrorService from '@/services/manageErrorService';
import moment from 'moment';
import reservationService from '@/services/reservationService';
import ReservationTagSelection from '@/components/commons/ReservationTagSelection';
import ReservationPlateSelection from '@/components/commons/ReservationPlateSelection';
import store from '@/store/';
import teletagService from '@/services/teletagService';
import plateService from '@/services/plateService';
import Advice from '@/components/commons/Advice';

export default {
  name: 'BookingEdit',
  components: {
    [ReservationDateTimePicker.name]: ReservationDateTimePicker,
    [AlternativeParkings.name]: AlternativeParkings,
    [ReservationTagSelection.name]: ReservationTagSelection,
    [ReservationPlateSelection.name]: ReservationPlateSelection,
    [Advice.name]: Advice
  },
  props: {
    parking: {
      type: Object,
      required: true
    },
    bookingData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      step: 1,
      isLoading: false,
      showAll: true,
      plates: [],
      minDate: this.calculeInitialDateTime(),
      inputDate: null,
      inputTime: '',
      tagSelected: { plate: '' },
      plateSelected: { alias: '' },
      booking: {
        parking: this.parking,
        entryDate: '',
        entryTime: ''
      },
      user: {
        tags: [],
        plates: []
      },
      plateSelectorKey: 0,
      currentTitle: 'reservation.step.1',
      validStep1: false,
      alternativeParkingList: [],
      warningMaxAvailability: moment(),
      warningClosingTime: null,
      warningReopeningTime: null,
      acceptMaxAvailability: false,
      acceptClosingTime: false,
      acceptWarningMessage: false,
      message: '',
      job: null
    };
  },
  computed: {
    restrictionAdvice() {
      if (
        this.parking.restrictions &&
        (this.parking.restrictions.notBeforeMinutes > 0 ||
          !this.parking.restrictions.notBeforeIsAbsolute)
      ) {
        let now = moment();
        let restriction = moment();
        if (!this.parking.restrictions.notBeforeIsAbsolute) {
          restriction = restriction.startOf('day');
        }
        restriction = restriction.add(
          this.parking.restrictions.notBeforeMinutes,
          'minutes'
        );
        return restriction.isAfter(now); // true if the restriction is in future
      } else {
        return false;
      }
    },
    showClosingWarning() {
      return (
        this.parking.blockAccessOnClosingTime === true &&
        !!this.warningClosingTime
      );
    },
    showReopeningWarning() {
      return this.showClosingWarning && !!this.warningReopeningTime;
    },
    parkingHasApr() {
      return this.parking.hasAPR;
    },
    parkingHasBle() {
      return this.parking.hasBLE;
    },
    parkingHasCamara() {
      return this.parking.hasCamara;
    },
    plateRules() {
      return [
        (v) =>
          /^[a-zA-Z0-9-]+$/.test(v) ||
          this.$i18n.t('plate.validation.valid.characters')
      ];
    },
    valid() {
      if (this.step === 1) {
        return this.validStep1;
      }
      if (this.step === 2) {
        return (
          this.acceptMaxAvailability &&
          (!this.showClosingWarning || this.acceptClosingTime)
        );
      }
      return true;
    },
    showBookingButton() {
      return this.step <= 2;
    },
    isLogged() {
      return store.state.isLogged;
    },
    entryFormatted() {
      const momentDate = this.getBookingEntryDateAsMoment();
      if (momentDate === null) {
        return '';
      }
      // Mismo formato que el resto de subtitulos. No pasa por formatDateAsUtc
      // porque esta fecha ya es la hora local elegida por el usuario, y ese
      // filtro la reinterpretaria como UTC y la desplazaria.
      return momentDate
        .locale(this.$i18n.locale)
        .format('DD MMMM, YYYY, HH:mm');
    },
    successClosingText() {
      const locale = this.$i18n.locale;
      const closingTime = this.$options.filters.formatDateAsUtc(
        this.warningClosingTime,
        locale
      );
      if (!this.showReopeningWarning) {
        return this.$i18n.t('reservation.success.closing.noReopening', {
          closingTime: closingTime
        });
      }
      return this.$i18n.t('reservation.success.closing', {
        closingTime: closingTime,
        reopeningTime: this.$options.filters.formatDateAsUtc(
          this.warningReopeningTime,
          locale
        )
      });
    },
    closingWarningText() {
      const locale = this.$i18n.locale;
      const closingTime = this.$options.filters.formatDateAsUtc(
        this.warningClosingTime,
        locale
      );
      if (!this.showReopeningWarning) {
        return this.$i18n.t('reservation.warning.closing.time.noReopening', {
          closingTime: closingTime
        });
      }
      return this.$i18n.t('reservation.warning.closing.time', {
        closingTime: closingTime,
        reopeningTime: this.$options.filters.formatDateAsUtc(
          this.warningReopeningTime,
          locale
        )
      });
    },
    maxAvailabilityFormatted() {
      return this.$options.filters.formatDateAsUtc(
        this.warningMaxAvailability,
        this.$i18n.locale
      );
    },
    maxAvailabilityTimeFormatted() {
      if (this.warningMaxAvailability === null) {
        return '';
      }
      let date = moment.utc(this.warningMaxAvailability);
      return this.$options.filters.formatTime(date);
    },
    maxAvailabilityDateFormatted() {
      if (this.warningMaxAvailability === null) {
        return '';
      }
      let date = moment.utc(this.warningMaxAvailability);
      return this.$options.filters.formatDate(date);
    },
    inputTimeFormatted() {
      let momentDate = this.getBookingEntryDateAsMoment();
      if (momentDate !== null) {
        return this.$options.filters.formatTime(momentDate.toDate());
      }
      return '';
    },
    inputDateFormatted() {
      let momentDate = this.getBookingEntryDateAsMoment();
      if (momentDate !== null) {
        return this.$options.filters.formatDate(momentDate.toDate());
      }
      return '';
    },
    warningRules() {
      return [(v) => v || this.$i18n.t('plate.validation.acceptWarning')];
    },
    hasObservationAsWarning() {
      if (_.isEmpty(this.parking)) {
        return false;
      }
      if (this.parking.description.observationWarning === null) {
        return false;
      }
      return typeof this.parking.description.observationWarning !== 'undefined';
    }
  },
  beforeDestroy() {
    this.job.stop();
  },
  async mounted() {
    if (this.isLogged && (this.parking.hasBLE || this.parking.hasCamara)) {
      this.loadPlates();
    } else if (this.isLogged && this.parking.hasAPR) {
      this.loadTacs();
    }

    let entryDateTime = moment.utc(this.bookingData.startLocal);
    this.inputDate = entryDateTime.toDate();
    this.inputTime = entryDateTime.format('HH:mm');

    this.booking.entryDate = entryDateTime.format('YYYY-MM-DD');
    this.booking.entryTime = this.inputTime;

    var CronJob = require('cron').CronJob;
    this.job = new CronJob(
      '*/1 * * * *',
      function () {
        this.minDate = this.calculeInitialDateTime();
      },
      null,
      false,
      this.parking.timezone,
      this
    );
    this.job.start();
  },
  methods: {
    calculeInitialDateTime() {
      let initial = moment();
      if (this.parking.restrictions) {
        if (this.parking.restrictions.notBeforeMinutes > 0) {
          let aux = _.cloneDeep(initial);
          if (!this.parking.restrictions.notBeforeIsAbsolute) {
            aux = aux.startOf('day');
          }
          aux = aux.add(this.parking.restrictions.notBeforeMinutes, 'minutes');
          if (aux.isAfter(initial)) {
            initial = aux;
          }
        }
      }

      let minutes = initial.minutes();
      let hours = initial.hours();
      if (minutes === 0) {
        minutes = 0;
      } else if (minutes <= 5) {
        minutes = 5;
      } else if (minutes <= 10) {
        minutes = 10;
      } else if (minutes <= 15) {
        minutes = 15;
      } else if (minutes <= 20) {
        minutes = 20;
      } else if (minutes <= 25) {
        minutes = 25;
      } else if (minutes <= 30) {
        minutes = 30;
      } else if (minutes <= 35) {
        minutes = 35;
      } else if (minutes <= 40) {
        minutes = 40;
      } else if (minutes <= 45) {
        minutes = 45;
      } else if (minutes <= 50) {
        minutes = 50;
      } else if (minutes <= 55) {
        minutes = 55;
      } else {
        minutes = 0;
        if (hours === 23) {
          hours = 0;
          this.form.day = moment().add(1, 'day').format('YYYY-MM-DD');
        } else {
          hours++;
        }
      }
      initial = initial
        .set('hours', hours)
        .set('minutes', minutes)
        .set('seconds', 0)
        .set('milliseconds', 0);

      return initial.toDate();
    },
    getExternalNearestTime(arg) {
      return this.$refs.reservationDateTimePicker.getExternalNearestTime(arg);
    },
    onPlateSelected(params) {
      this.plateSelected = params.plate;
    },
    onPlateAdded() {
      this.loadPlates();
    },
    onTagSelected(params) {
      this.tagSelected = params.tag;
    },
    onPlateChanged(params) {
      this.tagSelected.plate = params.plate;
    },
    onDateSelected(params) {
      if (params.date !== this.booking.entryDate) {
        this.booking.entryDate = params.date;
        if (this.booking.entryDate && this.booking.entryTime) {
          this.inputDate = moment(
            this.booking.entryDate + ' ' + this.booking.entryTime
          ).toDate();
        }
      }
    },
    onTimeSelected(params) {
      if (params.timeSelected !== this.booking.entryTime) {
        this.booking.entryTime = params.timeSelected;
        if (this.booking.entryDate && this.booking.entryTime) {
          this.inputDate = moment(
            this.booking.entryDate + ' ' + this.booking.entryTime
          ).toDate();
        }
      }
    },
    async loadPlates() {
      this.plateSelectorKey++;
      let results = await plateService.list({});

      if (results.error) {
        manageErrorService.handle(results.data);
        this.isLoading = false;
        this.valid = true;
        return;
      }

      let data = results.data;
      this.user.plates = data;
      if (data.length > 0) {
        this.plateSelected = data[0];
      }
    },
    async loadTacs() {
      let results = await teletagService.list({});

      if (results.error) {
        manageErrorService.handle(results.data);
        this.isLoading = false;
        this.valid = true;
        return;
      }

      let data = results.data;
      this.user.tags = data;
    },
    async checkAvailability() {
      if (this.userHasErrors()) {
        EventBus.$emit(keys.hideBookingTabs, {
          hidden: true
        });
        this.showAll = false;

        return;
      }
      this.isLoading = true;

      EventBus.$emit(keys.hideBookingTabs, {
        hidden: true
      });

      let entryDateLocal = this.getBookingEntryDateAsMoment();
      let params = {
        start: entryDateLocal.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        reservationId: this.bookingData.id
      };

      if (this.parkingHasApr) {
        params.teletagId = this.tagSelected.id;
        params.plate = this.tagSelected.plate;
      }

      let result = await reservationService.overlapped(params);

      if (result.error) {
        this.currentTitle = '';
        this.message = result.data.error;
        this.alternativeParkingList = result.data.alternatives;
        this.step = 100;
        this.isLoading = false;
        return;
      }

      if (result.data.shortMaxAvailability) {
        let closingApplies =
          this.parking.blockAccessOnClosingTime === true &&
          !!result.data.closingTimeLocal;
        let closingPending =
          closingApplies &&
          (!this.acceptClosingTime ||
            this.warningClosingTime !== result.data.closingTimeLocal);

        if (
          !this.acceptMaxAvailability ||
          this.warningMaxAvailability !== result.data.maxAvailabilityLocal ||
          closingPending
        ) {
          this.currentTitle = '';
          this.message = this.$i18n.t('reservation.warning.max.availability');
          this.warningMaxAvailability = result.data.maxAvailabilityLocal;
          this.warningClosingTime = result.data.closingTimeLocal;
          this.warningReopeningTime = result.data.reopeningTimeLocal;
          this.step = 2;
          this.isLoading = false;
          return;
        }
      }

      let reservationResult = await reservationService.edit(params);

      if (reservationResult.error) {
        manageErrorService.handle(reservationResult.data);
        this.isLoading = false;
        return;
      }
      this.job.stop();
      // El resumen muestra lo que quedó guardado, no la estimación del paso previo:
      // editMod recalcula la disponibilidad al persistir y puede salir distinta.
      this.warningMaxAvailability = reservationResult.data.maxAvailabilityLocal;
      this.warningClosingTime = reservationResult.data.closingTimeLocal;
      this.warningReopeningTime = reservationResult.data.reopeningTimeLocal;
      this.currentTitle = result.data.message;
      this.step = 200;
      this.isLoading = false;
      this.$mam.actionBooking();
    },
    getBookingEntryDateAsMoment() {
      if (
        _.isEmpty(this.booking.entryDate) ||
        _.isEmpty(this.booking.entryTime)
      ) {
        return null;
      }
      let selectedDate = moment(this.booking.entryDate).toDate();
      let hoursMinutes = this.booking.entryTime.split(':');
      return moment([
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        hoursMinutes[0],
        hoursMinutes[1]
      ]);
    },
    userHasErrors() {
      let user = store.state.user;
      let hasRoleBuyBalance = _.find(
        store.state.user.roles,
        (it) => it.authority === 'ROLE_BUY_BALANCE'
      );
      if (
        !this.parking.hasBLE &&
        !this.parking.hasCamara &&
        !this.parking.isFacility
      ) {
        if (this.parking.hasAPR && !user.activeTags) {
          this.$emit('on-user-error', { error: 'showErrorNoTags' });
          return true;
        }
      } else if (user.status !== 'ACTIVED') {
        this.$emit('on-user-error', { error: 'showErrorNotActived' });
        return true;
      } else if (this.parking.isFree) {
        if (user.balance.total < 0 && hasRoleBuyBalance) {
          this.$emit('on-user-error', { error: 'showErrorNoBalance' });
          return true;
        }
      } else if (!this.parking.isFree) {
        if (user.balance.total <= 0 && hasRoleBuyBalance) {
          this.$emit('on-user-error', { error: 'showErrorNoBalance' });
          return true;
        }
      }
      return false;
    },
    goToStep1() {
      this.minDate = this.calculeInitialDateTime();
      this.job.start();
      this.message = null;
      this.acceptMaxAvailability = false;
      this.acceptWarningMessage = false;
      EventBus.$emit(keys.hideBookingTabs, {
        hidden: false
      });
      this.currentTitle = 'reservation.step.1';
      this.step = 1;
    },
    goToCalendar() {
      store.commit('SET_RESTORE_HOME_DATA', { data: false });
      store.commit('CHANGE_SHOWING_PARKING_AS_LIST', { showAsList: false });

      this.$router.push({ path: this.$i18n.t('urlMyReservations') });
    },
    backLabel() {
      if (this.step === 200) {
        return this.$i18n.t('reservation.button.doAnother');
      } else {
        return this.$i18n.t('reservation.button.tryAgain');
      }
    },
    cancelEdit() {
      EventBus.$emit(keys.closeReservationDetail);
    }
  }
};
</script>

<style>
.v-text-field--outline.v-text-field--single-line input {
  margin-top: 10px !important;
}
</style>
